import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap, take, tap } from 'rxjs/operators';

import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { ErrorHandle, NoResultsTypes } from '@shared/no-results/entities/results-errors';
import { searchTypeToSearchBy } from '@shared/base-search-bar/mappers/search-by-and-type.mapper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { SearchType } from '@search-bar/entities/search-type';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { ButtonWidth } from '@design-system/components/molecules/button/entities/button.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyFontSize, TypographyColor } from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-no-results',
  templateUrl: './no-results.component.html',
  styleUrls: ['./no-results.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class NoResultsComponent {

  @Input() error: ErrorHandle;
  @Input() complementOfError: string;
  @Input() top: number = 20;
  @Input() messageError: string;
  @Input() searchType: SearchType;
  @Input() source: EVENT_SOURCE | string = EVENT_SOURCE.TAG;
  @Input() moreOptions = false;
  @Output() clickEvent: EventEmitter<void> = new EventEmitter<void>();

  protected AvoDispatch = new AvoDispatchOrchestrator();

  public readonly buttonWidth = ButtonWidth.Fit;
  
  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  
  public translateRoot = 'NO_RESULTS';

  constructor(
    private readonly router: Router,
    private readonly propertiesAmplitudeService: PropertiesAmplitudeService,
    private readonly avoService: AvoService,
  ) { }

  get withMessage(): boolean {
    return !!this.messageError;
  }

  get paddingTop(): string {
    return `${this.top}px`;
  }

  get translationError(): string {
    return `${this.translateRoot}.${this.message}`;
  }

  get research(): boolean {
    return this.error?.uiError === NoResultsTypes.Error;
  }

  private get message(): string {
    if (this.error?.uiError) {
      return this.formatUiError(this.error.uiError);
    } else {
      return this.formatUiError(NoResultsTypes.Empty);
    }
  }

  private get searchBy(): SearchBy {
    return searchTypeToSearchBy(this.searchType);
  }

  public showMoreOptions() {
    this.clickEvent.emit();
  }

  public async reload(): Promise<void> {
    const url = this.router.url;
    const navigateByDefault = await this.router.navigateByUrl('/', { skipLocationChange: true });
    if (navigateByDefault) {
      const redirect = await this.router.navigateByUrl(`/${url}`);
      if (redirect) {
        this.propertiesAmplitudeService.searchProperties$(this.searchBy).pipe(
          take(1),
          switchMap((searchProps) => this.avoService.getEventTriggerProps$(
            AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: this.source }, searchProps
          ).pipe(
            take(1),
            tap((trigger) => {
              this.AvoDispatch.selectTravelSearch({
                ...this.avoService.avoBaseProperties(trigger),
                source: this.avoService.getAvoSourceValueType(this.source),
              });
            })
          ))
        ).subscribe().unsubscribe();
      }
    }
  }

  private formatUiError(uiError: string): string {
    return this.complementOfError && uiError === NoResultsTypes.Empty ?
      `${uiError}_${this.complementOfError}` : uiError;
  }
}
